import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Globe, Mail } from "lucide-react";

const Privacy = () => {
  const lastUpdated = "February 14, 2025";

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-md border-b border-[#222]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Link to="/" className="text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-crimson flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold">PRIVACY POLICY</span>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-obsidian-light border border-[#222] p-8"
          >
            <p className="text-gray-500 text-sm mb-8">Last Updated: {lastUpdated}</p>

            <div className="prose prose-invert max-w-none space-y-8">
              {/* Introduction */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5 text-crimson" />
                  Introduction
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  Rekrutor ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered recruitment platform. By using Rekrutor, you consent to the practices described in this policy.
                </p>
              </section>

              {/* Information We Collect */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Database className="w-5 h-5 text-crimson" />
                  Information We Collect
                </h2>
                
                <h3 className="text-lg font-medium text-white mt-4 mb-2">Personal Information</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li><strong>Account Information:</strong> Name, email address, and profile photo when you sign up via Google OAuth</li>
                  <li><strong>Profile Data:</strong> Professional experience, skills, education, work history, and career preferences</li>
                  <li><strong>Resume/CV Data:</strong> Information extracted from uploaded documents including PDF resumes</li>
                  <li><strong>Job Application Data:</strong> Applications submitted, interview responses, and hiring preferences</li>
                  <li><strong>Communication Data:</strong> Messages exchanged through our platform</li>
                </ul>

                <h3 className="text-lg font-medium text-white mt-4 mb-2">Automatically Collected Information</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li><strong>Usage Data:</strong> Pages visited, features used, search queries, and interaction patterns</li>
                  <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
                  <li><strong>Location Data:</strong> General location based on IP address for job matching purposes</li>
                  <li><strong>Cookies:</strong> Session tokens and preferences stored locally</li>
                </ul>
              </section>

              {/* How We Use Information */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Eye className="w-5 h-5 text-crimson" />
                  How We Use Your Information
                </h2>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>Provide and improve our recruitment services and AI-powered matching</li>
                  <li>Generate personalized job recommendations and candidate matches</li>
                  <li>Power AI interviews and provide interview insights</li>
                  <li>Send notifications about job opportunities, messages, and platform updates</li>
                  <li>Process payments and manage subscriptions</li>
                  <li>Analyze platform usage to improve user experience</li>
                  <li>Prevent fraud and ensure platform security</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              {/* AI and Data Processing */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5 text-crimson" />
                  AI and Automated Processing
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Rekrutor uses artificial intelligence to enhance your experience:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li><strong>Profile Analysis:</strong> AI analyzes your profile to generate skills assessments and career insights</li>
                  <li><strong>Job Matching:</strong> Machine learning algorithms match candidates with suitable job opportunities</li>
                  <li><strong>AI Interviews:</strong> AI-powered interviews are recorded and analyzed to provide feedback</li>
                  <li><strong>Content Generation:</strong> AI assists in generating job descriptions and recommendations</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-4">
                  You can request human review of any AI-generated decisions that significantly affect you.
                </p>
              </section>

              {/* Data Sharing */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <UserCheck className="w-5 h-5 text-crimson" />
                  Information Sharing
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We share your information in the following circumstances:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li><strong>With Recruiters:</strong> When you apply for jobs or allow profile visibility</li>
                  <li><strong>With Job Seekers:</strong> Recruiters' job postings and company information are visible</li>
                  <li><strong>Service Providers:</strong> Third-party services that help us operate (e.g., payment processing, email delivery)</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong>Business Transfers:</strong> In connection with mergers or acquisitions</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-4">
                  We never sell your personal information to third parties.
                </p>
              </section>

              {/* Data Security */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5 text-crimson" />
                  Data Security
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  We implement industry-standard security measures to protect your data:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mt-4">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Secure authentication via OAuth 2.0</li>
                  <li>Regular security audits and monitoring</li>
                  <li>Access controls and employee training</li>
                </ul>
              </section>

              {/* Your Rights */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5 text-crimson" />
                  Your Rights
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Depending on your location, you may have the following rights:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Correction:</strong> Update inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                  <li><strong>Portability:</strong> Receive your data in a portable format</li>
                  <li><strong>Objection:</strong> Object to certain processing activities</li>
                  <li><strong>Withdraw Consent:</strong> Revoke consent for optional processing</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-4">
                  To exercise these rights, contact us at privacy@rekrutor.com
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Mail className="w-5 h-5 text-crimson" />
                  Contact Us
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  For privacy-related questions or concerns:
                </p>
                <div className="mt-4 p-4 bg-obsidian border border-[#333]">
                  <p className="text-white font-medium">Rekrutor Privacy Team</p>
                  <p className="text-gray-400">Email: privacy@rekrutor.com</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#222] py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex justify-between items-center">
          <p className="text-gray-500 text-sm">© 2025 Rekrutor. All rights reserved.</p>
          <div className="flex gap-4 text-sm">
            <Link to="/terms" className="text-gray-400 hover:text-crimson">Terms</Link>
            <Link to="/support" className="text-gray-400 hover:text-crimson">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
